import { useCallback, useEffect, useState } from "react";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { Plus, User } from "lucide-react";

import { AddMemberSheet } from "@/components/account/family/add-member-sheet";
import { handleVaultLocked } from "@/lib/vault-unlock";

export interface VaultMemberItem {
  id: string;
  name: string;
  relationship: string;
  role: string;
  avatarUrl: string | null;
  _count: { journalEntries: number };
}

/** Localized label for a vault-member relationship. "self" and unknown values
 *  return "" so the caller falls back to the raw relationship string. */
function relationshipLabel(relationship: string, t: ReturnType<typeof useLingui>["t"]): string {
  switch (relationship) {
    case "spouse":
      return t(msg`Spouse`);
    case "child":
      return t(msg`Child`);
    case "parent":
      return t(msg`Parent`);
    case "sibling":
      return t(msg`Sibling`);
    case "other":
      return t(msg`Other`);
    default:
      return "";
  }
}

interface VaultMembersResponse {
  members?: VaultMemberItem[];
}

export function VaultMemberSwitcher({
  activeMemberId,
  onMemberChange,
}: {
  activeMemberId: string | null;
  onMemberChange: (memberId: string | null) => void;
}) {
  const { t } = useLingui();
  const [members, setMembers] = useState<VaultMemberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      const data = await apiFetch.get<VaultMembersResponse | VaultMemberItem[]>(
        "/api/account/vault/members",
      );
      if (Array.isArray(data)) {
        setMembers(data);
      } else {
        setMembers(data.members ?? []);
      }
    } catch (err) {
      // Locked meanwhile: the PIN gate comes back. Otherwise swallow; the
      // switcher will simply show empty state.
      handleVaultLocked(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleMemberAdded = useCallback(
    (newMember: VaultMemberItem) => {
      setMembers((prev) => [...prev, newMember]);
      setAddModalOpen(false);
      onMemberChange(newMember.id);
    },
    [onMemberChange],
  );

  if (loading) {
    return (
      <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[0, 1].map((i) => (
          <div key={i} className="bg-muted h-10 w-24 animate-pulse rounded-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {members.map((member) => {
          const isActive = activeMemberId ? member.id === activeMemberId : member.id === "self";
          const relLabel =
            member.relationship !== "self"
              ? relationshipLabel(member.relationship, t) || member.relationship
              : null;

          return (
            <button
              key={member.id}
              type="button"
              onClick={() => onMemberChange(member.id === "self" ? null : member.id)}
              className={cn(
                "flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap transition-all",
                "min-h-[44px]",
                isActive
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground",
              )}
            >
              {member.avatarUrl ? (
                <img src={member.avatarUrl} alt="" className="size-6 rounded-full object-cover" />
              ) : (
                <User className="size-4" />
              )}
              <span>{member.name || (member.id === "self" ? t`You` : "")}</span>
              {relLabel && <span className="text-muted-foreground text-xs">({relLabel})</span>}
            </button>
          );
        })}

        {/* Add member pill */}
        <button
          type="button"
          onClick={() => setAddModalOpen(true)}
          aria-label={t`Add family member`}
          className={cn(
            "flex shrink-0 items-center gap-1.5 rounded-full border border-dashed px-4 py-2 text-sm font-medium whitespace-nowrap transition-all",
            "min-h-[44px]",
            "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
          )}
        >
          <Plus className="size-4" />
          <Trans>Add member</Trans>
        </button>
      </div>

      <AddMemberSheet
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdded={(member) => {
          if (member) handleMemberAdded(member as VaultMemberItem);
          else setAddModalOpen(false);
        }}
        apiTarget="vault"
        currentMemberCount={members.filter((m) => m.id !== "self").length}
      />
    </>
  );
}
